import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import * as z from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { thunks } from "src/thunks/topic";
import { useDispatch, useSelector } from "src/store";
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import { Topic } from "src/types/topic";
import moment from "moment";
import { TextEditor } from "src/components/core/text-editor/text-editor";
import { TemplateButton } from "../common/editor/template-button";
import { ContentGoalType } from "src/types/content-goal-type";
import { Editor } from "@tiptap/react";
import { PromptMessage, PromptMessageUser } from "src/types/prompts";
import { PROMPTS } from "src/consts";
import { thunks as promptsThunks } from "src/thunks/prompts";
import axios from "axios";
import { CancelTokenSource } from "axios";
import { DOMSerializer } from "prosemirror-model";
import { MagicWand as MagicWandIcon } from "@phosphor-icons/react/dist/ssr/MagicWand";
import { ArrowsOutLineHorizontal as ArrowsHorizontalIcon } from "@phosphor-icons/react/dist/ssr/ArrowsOutLineHorizontal";
import { ArrowsInLineHorizontal as ArrowsInHorizontalIcon } from "@phosphor-icons/react/dist/ssr/ArrowsInLineHorizontal";
import { TopicContentGenerationForm } from "./topic-content-generation-form";

// Define the Zod schema
const createValidationSchema = (t: any) =>
  z.object({
    id: z.string().optional(),
    name: z
      .string()
      .max(255, t(tokens.general.validators.maxLength255) as string)
      .min(1, t(tokens.general.validators.required) as string),
    description: z.string().optional(),
    content: z.string().optional(),
    storyLineId: z.string().optional(),
    productId: z.string().optional(),
    date: z.date().optional(),
    contentGoalType: z.nativeEnum(ContentGoalType),
  });

// Define the Values type using Zod inference
type FormValues = z.infer<ReturnType<typeof createValidationSchema>>;

interface TopicGenericEditFormProps {
  topic?: Topic;
  hideBorder?: boolean;
  onSubmitStatus: (isSubmitting: boolean) => void;
  onSubmit?: (topicId: string) => void;
  date?: Date | null;
  ref: any;
}

export type TopicGenericEditHandle = {
  handleSubmit: () => void;
};

export const TopicGenericEditForm = forwardRef<
  TopicGenericEditHandle,
  TopicGenericEditFormProps
>((props, ref) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { topic, onSubmitStatus, onSubmit, hideBorder, date } = props;
  const editorRef = useRef<Editor | null>(null);

  const { strategyId } = useSelector((state) => state.settings);

  // Create the validation schema
  const validationSchema = createValidationSchema(t);

  // Initialize the form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      id: topic?.id,
      name: topic?.name || "",
      description: topic?.description || "",
      content: topic?.content || "",
      productId: topic?.product?.id || "",
      storyLineId: topic?.storyLine?.id || "",
      contentGoalType: topic?.contentGoalType || ContentGoalType.None,
      date: topic?.date
        ? moment(topic.date).toDate()
        : date
          ? moment(date).toDate()
          : undefined,
    },
    resolver: zodResolver(validationSchema),
  });

  useImperativeHandle(ref, () => ({
    handleSubmit: () => handleSubmit(onSubmitForm)(),
  }));

  const [cancelTokenSource, setCancelTokenSource] = useState<CancelTokenSource>(
    axios.CancelToken.source()
  );

  const [isAIGenerationVisible, setIsAIGenerationVisible] = useState(false);

  const getSelectedHtml = (editor: Editor) => {
    const { state, view } = editor;
    const { from, to } = state.selection;
    const slice = state.doc.slice(from, to);

    const fragment = DOMSerializer.fromSchema(editor.schema).serializeFragment(
      slice.content
    );

    const div = document.createElement("div");
    div.appendChild(fragment);

    return div.innerHTML;
  };

  const generateContent = useCallback(
    async (editor: Editor, prompt: string, skipSelection: boolean = false) => {
      //const { state, dispatch } = editor.view;
      //const { selection, tr } = state;
      if (editor.state.selection.empty) {
        return false;
      }

      const selectedHtml = getSelectedHtml(editor);

      // const selectedText = editor.state.doc.textBetween(
      //   editor.state.selection.from,
      //   editor.state.selection.to,
      //   " "
      // );

      var messages = [
        {
          user: PromptMessageUser.System,
          content: `${prompt}. Only respond with the content, no other text.`,
        },
        {
          user: PromptMessageUser.Assistant,
          content: "Here is the full content:\n" + editor.getHTML(),
        },
      ];

      if (!skipSelection) {
        messages.push({
          user: PromptMessageUser.User,
          content: `Here is the selected text:\n${selectedHtml}`,
        });
      }

      await dispatch(
        promptsThunks.runChatPrompt(
          strategyId!,
          i18n.language,
          {
            messages: messages,
          },
          (text) => {},
          () => {
            toast.error(t(tokens.general.formError));
          },
          (text) => {
            editor.commands.insertContent(text);
            editor.commands.focus();
            // Handle completion if needed
          },
          cancelTokenSource
        )
      );
    },
    [cancelTokenSource, dispatch, i18n.language, strategyId, t]
  );

  const onSubmitForm = async (values: FormValues): Promise<void> => {
    onSubmitStatus(true);
    try {
      if (values.id) {
        await dispatch(
          thunks.updateTopic(strategyId!, values.id, {
            name: values.name,
            date: values.date ? moment(values.date).valueOf() : null,
            description: values.description || "",
            content: values.content || "",
            productId: values.productId,
            storyLineId: values.storyLineId,
            contentGoalType: values.contentGoalType,
          })
        );
        onSubmit?.(values.id);
        toast.success(t(tokens.topics.details.form.updateSuccessMessage));
      } else {
        const newTopic = await dispatch(
          thunks.createTopic(strategyId!, {
            name: values.name,
            date: values.date ? moment(values.date).valueOf() : null,
            description: values.description || "",
            content: values.content || "",
            productId: values.productId,
            storyLineId: values.storyLineId,
            contentGoalType: values.contentGoalType,
          })
        );
        onSubmit?.(newTopic.id);
        setValue("id", newTopic.id);
        toast.success(t(tokens.topics.details.form.createSuccessMessage));
      }
    } catch (error) {
      toast.error(t(tokens.general.formError));
      setError("root", { message: "Submission failed" });
    } finally {
      onSubmitStatus(false);
    }
  };

  return (
    <Stack
      spacing={4}
      sx={{
        overflow: hideBorder ? "auto" : "inherit",
      }}
    >
      {/* {!isAIGenerationVisible && (
        <Box>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<MagicWandIcon />}
            onClick={() => setIsAIGenerationVisible((prev) => !prev)}
          >
            AI GENERATE
          </Button>
        </Box>
      )} */}
      {/* {isAIGenerationVisible && ( */}
      <TopicContentGenerationForm
        hasTitle={!!topic?.name}
        isCollapsed={!!topic?.name}
        onTitleGenerate={(title) => {
          setValue("name", title);
        }}
        onTextGenerate={(text) => {
          console.log(text);
          editorRef?.current?.commands.setContent(text);
        }}
        onFinish={() => {
          setValue("content", editorRef?.current?.getHTML() || "");
        }}
      />
      {/* )} */}
      <Card
        sx={{
          height: "100%",
          overflow: "auto",
          borderRadius: hideBorder ? 0 : 2,
        }}
      >
        <CardContent>
          <Stack spacing={3}>
            <form onSubmit={handleSubmit(onSubmitForm)}>
              <Stack spacing={3}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      error={!!errors.name}
                      fullWidth
                      helperText={errors.name?.message}
                      label={t(tokens.topics.details.form.name)}
                    />
                  )}
                />

                {/* <Stack
                  spacing={3}
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                >
                  <Box>
                    <TextField
                      select
                      sx={{
                        width: "200px",
                      }}
                      error={!!errors.contentGoalType}
                      label={t(tokens.storylines.details.form.contentGoal)}
                      helperText={errors.contentGoalType?.message}
                      // ... other props for contentGoalType dropdown
                    >
                      {/* MenuItem options */}
                {/* </TextField>
                  </Box>
                </Stack> */}
              </Stack>
            </form>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                }}
              >
                {t(tokens.topics.details.form.content)}
              </Typography>
              <Controller
                name="content"
                control={control}
                render={({ field }) => (
                  <TextEditor
                    showHeaderTool={true}
                    ref={editorRef}
                    content={field.value || ""}
                    onUpdate={(content) => {
                      setValue("content", content.editor.getHTML());
                    }}
                    onCustomPrompt={async (prompt) => {
                      await generateContent(editorRef.current!, prompt, true);
                    }}
                    menuItems={[
                      {
                        title: t(tokens.general.ai.simplify),
                        onClick: async (editor: Editor) => {
                          await generateContent(editor, PROMPTS.SIMPLIFY);
                        },
                        icon: <MagicWandIcon />,
                      },
                      {
                        title: t(tokens.general.ai.makeShorter),
                        onClick: async (editor: Editor) => {
                          await generateContent(editor, PROMPTS.MAKE_SHORTER);
                        },
                        icon: <ArrowsInHorizontalIcon />,
                      },
                      {
                        title: t(tokens.general.ai.makeLonger),
                        onClick: async (editor: Editor) => {
                          await generateContent(editor, PROMPTS.MAKE_LONGER);
                        },
                        icon: <ArrowsHorizontalIcon />,
                      },
                    ]}
                    placeholder={t(tokens.general.placeholders.editor)}
                    customButtons={
                      [
                        // <TemplateButton
                        //   key="template-button"
                        //   editor={editorRef.current}
                        //   getMessages={() => {
                        //     const name = control._formValues.name;
                        //     const description = control._formValues.description;
                        //     var messages = [
                        //       {
                        //         user: PromptMessageUser.System,
                        //         content: PROMPTS.LANDING_PAGE,
                        //       },
                        //     ] as PromptMessage[];
                        //     if (name) {
                        //       messages.push({
                        //         user: PromptMessageUser.User,
                        //         content: `Topic title:\n ${name}`,
                        //       });
                        //     }
                        //     if (description) {
                        //       messages.push({
                        //         user: PromptMessageUser.User,
                        //         content: `Topic description:\n ${description}`,
                        //       });
                        //     }
                        //     return messages;
                        //   }}
                        //   onTextGenerate={(text) => {
                        //     editorRef?.current?.commands.setContent(text);
                        //   }}
                        //   onFinish={() => {
                        //     setValue(
                        //       "content",
                        //       editorRef?.current?.getHTML() || ""
                        //     );
                        //   }}
                        // />,
                      ]
                    }
                  />
                )}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
});
